import React, { useState, useEffect } from 'react';
import Toggle from '../../../components/ui/toggle';
import { Filter, Loader2 } from 'lucide-react';
import ChatListItem from './ChatListItem';
import AgentTogglePanel from '../../agents/components/TogglePanel/AgentTogglePanel';
import { leadsApi, LeadWithAgent } from '../services/leadsApi';

export interface ChatListProps {
    selectedChatId?: string;
    onChatSelect: (lead: LeadWithAgent) => void;
    flowId?: string;
    agentId?: string;
    initialLeadId?: string;
}

const ChatList: React.FC<ChatListProps> = ({ selectedChatId, onChatSelect, flowId, agentId, initialLeadId }) => {
    const [activeFilter, setActiveFilter] = useState<'activos' | 'archivados'>('activos');
    const [agentModalOpen, setAgentModalOpen] = useState(false);
    const [leads, setLeads] = useState<LeadWithAgent[]>([]);
    const [loading, setLoading] = useState(true);

    const agent = {
        id: '1',
        name: 'Agente Oscar Ramos',
        status: "active" as "active",
        description: 'Ejemplo de agente para el modal',
        profileImage: '',
    };

    useEffect(() => {
        const fetchLeads = async () => {
            try {
                setLoading(true);
                console.log('[ChatList] 🔄 Cargando leads con filtros:', { flowId, agentId });
                const data = await leadsApi.getLeads(flowId, agentId);
                console.log('[ChatList] ✅ Leads cargados:', data.length, data);
                setLeads(data);

                // Auto-select lead if initialLeadId is provided
                if (initialLeadId) {
                    const target = data.find(l => l.id === initialLeadId);
                    if (target) {
                        onChatSelect(target);
                    }
                }
            } catch (error) {
                console.error('[ChatList] ❌ Error fetching leads:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchLeads();
    }, [flowId, agentId]);

    const filteredLeads = leads.filter(lead => {
        const tag = lead.tags?.toLowerCase() || '';
        if (activeFilter === 'activos') {
            return tag !== 'archivado' && tag !== 'cerrado';
        } else {
            return tag === 'archivado' || tag === 'cerrado';
        }
    });

    const getStatusFromTags = (tags?: string | null): 'nuevo' | 'respondido' | 'archivado' => {
        const tag = tags?.toLowerCase() || 'nuevo';
        if (tag === 'archivado' || tag === 'cerrado') return 'archivado';
        if (tag === 'respondido') return 'respondido';
        return 'nuevo';
    };

    const handleLeadClick = (lead: LeadWithAgent) => {
        console.log('[ChatList] 👆 Click en lead:', lead);
        console.log('[ChatList] 📱 telefono_lead del lead:', lead.telefono_lead);
        onChatSelect(lead);
    };

    return (
        <div className="w-80 bg-white rounded-xl border-r border-gray-200 flex flex-col h-full min-h-0">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                <span className="font-semibold text-maxi-text-dark">Agente</span>
            </div>
            {/* Filter Buttons */}
            <div className="p-4 border-b border-gray-200 text-center">
                <Toggle
                    options={[
                        { label: 'Activos', value: 'activos', icon: <Filter className="w- h-4" /> },
                        { label: 'Archivados', value: 'archivados', icon: <Filter className="w-4 h-4" /> }
                    ]}
                    value={activeFilter}
                    onChange={v => setActiveFilter(v as 'activos' | 'archivados')}
                    className="w-[8hv]"
                />
            </div>

            {/* Chat List */}
            <div className="flex-1 overflow-y-auto">
                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                ) : filteredLeads.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                        No hay conversaciones
                    </div>
                ) : (
                    filteredLeads.map((lead) => (
                        <ChatListItem
                            key={lead.id}
                            contactName={lead.name || lead.telefono_lead || 'Sin nombre'}
                            platform="WhatsApp"
                            status={getStatusFromTags(lead.tags)}
                            isSelected={selectedChatId === lead.id}
                            onClick={() => handleLeadClick(lead)}
                        />
                    ))
                )}
            </div>
            {/* Agent Modal */}
            {agentModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <AgentTogglePanel
                        agent={agent}
                        isOpen={agentModalOpen}
                        onClose={() => setAgentModalOpen(false)}
                    />
                </div>
            )}
        </div>
    );
};

export default ChatList;
